import { Badge, Button, Icons, Tooltip, useToggle } from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { ReactNode, SVGProps } from 'react';
import { PreviewItemModal } from '../../../components/PreviewModal';
import { DataPointButton } from './DataPointButton';

export type TDataPointAction = {
  icon: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  tooltip: string;
  onClick: () => void;
};

export const DataPoint = ({
  onCopy,
  onDelete,
  editConfig,
  document,
  title,
  value,
  description,
  type = 'text',
  className,
  disabled,
  hideAddAction = false,
  reactNode,
  actions,
}: {
  title: string;
  value: string | null | React.ReactNode;
  document?: {
    isSupported: boolean;
    fileName: string | null;
    fileUrl: string | null;
    id: number | null;
    onViewDocument: () => void;
  };
  editConfig?: {
    isAllowed: boolean;
    onEdit: () => void;
  };
  onDelete?: () => void;
  onCopy?: () => void;
  description?: string;
  type?: 'text' | 'password' | ReactNode;
  className?: string;
  disabled?: boolean;
  hideAddAction?: boolean;
  reactNode?: ReactNode;
  actions?: TDataPointAction[];
}) => {
  const shouldShowAddButton =
    !disabled &&
    !hideAddAction &&
    (!value || (document && document.isSupported && !document.id));

  const addButtonCopy = !value ? 'Add' : 'Add document';

  const documentPreviewVisibility = useToggle();
  const passwordVisibility = useToggle(false);

  async function openDocumentPreview() {
    if (!document?.id || !document?.fileName || !document?.fileUrl) return;
    documentPreviewVisibility.on();
  }

  const displayValue = () => {
    if (!value) return null;

    if (type === 'password') {
      return passwordVisibility.state
        ? value
        : '•'.repeat(Math.min(16, value.toString().length));
    }

    return value;
  };

  return (
    <>
      {documentPreviewVisibility.state && document?.fileUrl && (
        <PreviewItemModal
          isOpen={documentPreviewVisibility.state}
          onClose={documentPreviewVisibility.off}
          previewFile={{
            id: document?.id,
            fileName: document?.fileName,
            fileUrl: document?.fileUrl,
            documentType: LegacyDocumentType.Other, // This component doesn't really need documentType so I can pass anything
          }}
        />
      )}
      <div
        data-testid={`datapoint-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className={classNames(
          'flex flex-col xl:flex-row bg-bg-primary min-h-14 relative',
          className
        )}
      >
        <div className="flex xl:justify-between w-full pt-lg pb-xs pr-3xl xl:py-lg xl:pr-0 xl:min-w-[300px] xl:w-2/5">
          <div className="px-xl py-sm txt-small-medium text-text-quaternary min-w-0 xl:flex-1 flex items-start break-words">
            {title}
          </div>
          {description ? (
            <div className="flex h-8 justify-center items-center">
              <Tooltip content={description} side="top">
                <Icons.InfoCircle className="size-4 text-text-quaternary hover:text-fg-quaternary-hover mr-xl" />
              </Tooltip>
            </div>
          ) : null}
        </div>
        <div className="pt-xs pb-lg px-xl xl:py-lg flex gap-xl w-full min-w-0 items-start">
          <div className="min-w-0 flex-1 flex gap-xl items-start">
            {value && (
              <div className="min-w-0 py-sm txt-small-regular">
                {typeof value === 'string' ? (
                  <span className="whitespace-pre-wrap break-words">
                    {displayValue()}
                  </span>
                ) : (
                  displayValue()
                )}
              </div>
            )}
            {disabled && !value && (
              <Badge size="sm" variant="warning" className="my-sm">
                Info missing
              </Badge>
            )}
            {reactNode}
            {shouldShowAddButton && (
              <Button
                variant="tertiary"
                size="sm"
                onClick={editConfig?.onEdit}
                className="h-8 text-fg-brand hover:text-fg-brand-hover"
              >
                <Icons.Plus className="size-4" />
                <span>{addButtonCopy}</span>
              </Button>
            )}
          </div>
          {value || document?.id || actions?.length ? (
            <div className="flex items-center gap-lg shrink-0 self-center">
              {type === 'password' && value && (
                <DataPointButton
                  onClick={passwordVisibility.toggle}
                  tooltip={passwordVisibility.state ? 'Hide' : 'Show'}
                  icon={passwordVisibility.state ? Icons.EyeOff : Icons.Eye}
                />
              )}
              {actions?.map((action) => (
                <DataPointButton
                  key={action.tooltip}
                  onClick={action.onClick}
                  tooltip={action.tooltip}
                  icon={action.icon}
                />
              ))}
              {onCopy && (
                <DataPointButton
                  onClick={onCopy}
                  tooltip="Copy"
                  icon={Icons.Copy06}
                />
              )}
              {document && document.isSupported && document.id && (
                <DataPointButton
                  onClick={() => {
                    openDocumentPreview();
                  }}
                  tooltip="View supporting document"
                  icon={Icons.File06}
                />
              )}
              {editConfig && !disabled && (
                <DataPointButton
                  onClick={editConfig.onEdit}
                  tooltip="Edit"
                  icon={Icons.Edit02}
                />
              )}
              {onDelete && !disabled && (
                <DataPointButton
                  onClick={onDelete}
                  tooltip="Delete"
                  icon={Icons.Trash01}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};
